import { Pencil, Trash2, Upload } from "lucide-react";
import { useRef, useState } from "react";

import { uploadUnitImage } from "@/api/organization";

const API_URL = import.meta.env.VITE_API_URL as string;

interface OrganizationBannerProps {
  imageUrl?: string;
  unitId?: string;
  onImageChange: (newUrl: string | null) => void;
}

const OrganizationBanner = ({
  imageUrl,
  unitId,
  onImageChange
}: OrganizationBannerProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resolvedUrl = imageUrl
    ? imageUrl.startsWith("http")
      ? imageUrl
      : `${API_URL}/${imageUrl.replace(/^[/\\]/, "")}`
    : null;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const response = await uploadUnitImage(unitId ?? "0", file);
      if (response?.imageUrl) {
        onImageChange(response.imageUrl.replace(/^[/\\]/, ""));
      }
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = () => {
    onImageChange(null);
  };

  return (
    <div
      className="relative w-full cursor-pointer select-none overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {resolvedUrl ? (
        <img
          src={resolvedUrl}
          alt="organization banner"
          className="w-full object-cover"
          style={{ maxHeight: "480px", minHeight: "140px" }}
        />
      ) : (
        <div
          className="flex w-full items-center justify-center bg-gradient-to-b from-[#162b41] to-[#033d61]"
          style={{ height: "220px" }}
        >
          <Upload size={64} className="text-white/20" />
        </div>
      )}

      {/* Hover overlay */}
      <div
        className={`absolute inset-0 flex items-center justify-center gap-4 bg-black/50 transition-opacity duration-200 ${isHovered ? "opacity-100" : "pointer-events-none opacity-0"}`}
      >
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="flex items-center gap-2 rounded-lg bg-white/90 px-4 py-2.5 text-sm font-medium text-gray-800 shadow transition-colors hover:bg-white"
        >
          <Pencil size={16} />
          {isUploading ? "درحال آپلود..." : "تغییر عکس"}
        </button>

        {resolvedUrl && (
          <button
            type="button"
            onClick={handleDelete}
            className="flex items-center gap-2 rounded-lg bg-red-600/90 px-4 py-2.5 text-sm font-medium text-white shadow transition-colors hover:bg-red-600"
          >
            <Trash2 size={16} />
            حذف عکس
          </button>
        )}

        {!resolvedUrl && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="flex items-center gap-2 rounded-lg bg-blue-600/90 px-4 py-2.5 text-sm font-medium text-white shadow transition-colors hover:bg-blue-600"
          >
            <Upload size={16} />
            آپلود عکس
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
};

export default OrganizationBanner;
